import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DateService } from '@services/shared/date.service';
import { SharedModule } from '@shared/modules/shared.module';
import { activeLocale } from 'app/core/utils/locale';
import { AvatarComponent } from '../../avatar/avatar.component';
import { ButtonComponent } from '../../button/button.component';
import { CheckboxComponent } from '../../checkbox/checkbox.component';
import { TagComponent } from '../../tag/tag.component';
import { isTagData, TableAction, TableColumn, TableRow, TableRowId, TagData } from '../table.model';
import { cellText, getCellValue, getNestedValue } from '../table-cell.util';

/**
 * Mobile view of app-table: an accordion card per row, empty / load-error card, and the infinite-scroll
 * sentinel. Presentational: the rows, the expanded ids and the paging state stay in TableComponent
 * (this view is re-created each time the skeleton shows), which reacts to the outputs.
 */
@Component({
	selector: 'app-table-mobile-list',
	imports: [SharedModule, AvatarComponent, ButtonComponent, CheckboxComponent, TagComponent],
	templateUrl: './table-mobile-list.component.html',
	styleUrl: './table-mobile-list.component.scss',
})
export class TableMobileListComponent<T extends TableRow = TableRow> implements OnDestroy {
	@Input() items: T[] = [];
	@Input() columns: TableColumn<T>[] = [];
	@Input() mainField = '';
	@Input() loadError = false;
	/** Ids of the open cards (owned by the table so they survive reloads) */
	@Input() expandedIds = new Set<TableRowId>();
	@Input() hasMore = false;
	@Input() infiniteLoading = false;

	@Input() hideAccordionArrow = false;
	@Input() showMobileNavigateArrow = false;
	@Input() showMobileDownloadButton = false;
	@Input() showMobileCheckboxes = false;
	@Input() showOptionsButton = true;
	@Input() showNavigationButton = false;
	@Input() navigationButtonLabel = '';
	@Input() mobileSubtitleField = '';
	@Input() mobileSubtitleIcon = '';
	@Input() mobileSubtitleEmptyKey = '';
	@Input() mobileAccordionFields: string[] = [];
	@Input() getMenuActions?: (rowData: T) => TableAction[];

	@Output() retry = new EventEmitter<void>();
	@Output() rowSelect = new EventEmitter<T>();
	@Output() toggle = new EventEmitter<TableRowId | undefined>();
	@Output() checkboxChange = new EventEmitter<T>();
	@Output() menuOpen = new EventEmitter<{ origin: EventTarget | null; actions: TableAction[]; item: T }>();
	@Output() download = new EventEmitter<T>();
	@Output() navigate = new EventEmitter<T>();
	/** The end of the list is (almost) in view: the table asks the parent for the next page */
	@Output() nearEnd = new EventEmitter<void>();

	private infiniteObserver?: IntersectionObserver;
	private infiniteSentinel?: HTMLElement;

	constructor(
		private dateService: DateService,
		private translocoService: TranslocoService,
	) {}

	@ViewChild('infiniteSentinel')
	set infiniteSentinelRef(ref: ElementRef<HTMLElement> | undefined) {
		this.infiniteObserver?.disconnect();
		this.infiniteSentinel = ref?.nativeElement;
		if (!this.infiniteSentinel || typeof IntersectionObserver === 'undefined') return;
		this.infiniteObserver = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting)) this.nearEnd.emit();
			},
			{ rootMargin: '0px 0px 300px 0px' },
		);
		this.infiniteObserver.observe(this.infiniteSentinel);
	}

	ngOnDestroy(): void {
		this.infiniteObserver?.disconnect();
	}

	/** A short page may leave the sentinel on screen: the observer won't fire again, so check by hand */
	checkSentinelInView(): void {
		if (!this.infiniteSentinel) return;
		if (this.infiniteSentinel.getBoundingClientRect().top < window.innerHeight + 300) {
			this.nearEnd.emit();
		}
	}

	isExpanded(id: TableRowId | undefined): boolean {
		return id !== undefined && this.expandedIds.has(id);
	}

	menuActionsOf(item: T): TableAction[] {
		return this.getMenuActions?.(item) || [];
	}

	private cellValue(item: T, field: string): unknown {
		return getCellValue(item, field, this.columns);
	}

	/** Display value of the main field in the accordion header */
	getMainFieldDisplayValue(rowData: T): string {
		const value = this.cellValue(rowData, this.mainField);
		return value === null || value === undefined ? '' : String(value);
	}

	/** Subtitle under the main field; null when empty, whitespace or the '-' placeholder */
	getMobileSubtitleValue(rowData: T): string | null {
		if (!this.mobileSubtitleField) return null;
		const value = this.cellValue(rowData, this.mobileSubtitleField);
		if (value === null || value === undefined) return null;
		const stringValue = String(value).trim();
		if (!stringValue || stringValue === '-') return null;
		return stringValue;
	}

	hasMobileSubtitleValue(rowData: T): boolean {
		const value = this.getMobileSubtitleValue(rowData);
		return value !== null && value !== undefined && value !== '';
	}

	/** Columns shown inside an open card: never the main or subtitle field; only mobileAccordionFields if set */
	shouldShowInAccordion(col: TableColumn<T>): boolean {
		if (col.field === this.mainField) return false;
		if (col.field === this.mobileSubtitleField) return false;
		if (this.mobileAccordionFields.length > 0) {
			return this.mobileAccordionFields.includes(col.field);
		}
		return true;
	}

	tagOf(item: T, field: string): TagData | null {
		const value = this.cellValue(item, field);
		return isTagData(value) ? value : null;
	}

	shouldShowAvatar(item: T): boolean {
		const avatarColumn = this.columns.find((col) => col.showAvatar);
		if (avatarColumn) {
			return true;
		}
		return !!this.fallbackAvatar(item);
	}

	hasImageField(): boolean {
		const avatarColumn = this.columns.find((col) => col.showAvatar);
		return !!avatarColumn?.imageField;
	}

	getAvatarImage(item: T): string | undefined {
		const avatarColumn = this.columns.find((col) => col.showAvatar);
		if (avatarColumn?.imageField) {
			return cellText(getNestedValue(item, avatarColumn.imageField));
		}
		return this.fallbackAvatar(item);
	}

	/** Image of a row without an avatar column: the usual photo fields, if the row has any */
	private fallbackAvatar(item: T): string | undefined {
		const row = item as {
			photoUrl?: string;
			imageUrl?: string;
			avatarUrl?: string;
			employee?: { photoUrl?: string };
		};
		return row.photoUrl || row.imageUrl || row.employee?.photoUrl || row.avatarUrl || undefined;
	}

	/** User id for the avatar's profile photo: only with userIdField (item.id is not necessarily a user id) */
	getAvatarUserId(item: T): number | undefined {
		const avatarColumn = this.columns.find((col) => col.showAvatar);
		const idField = avatarColumn?.userIdField;
		if (idField) {
			const id = getNestedValue(item, idField);
			return id ? Number(id) : undefined;
		}
		return undefined;
	}

	/** Cell value for the open card: dates as DD/MM/YYYY, numbers with the active language's separators */
	getFormattedCellValue(item: T, field: string): string {
		const value = this.cellValue(item, field);

		if (value === null || value === undefined || value === '') return '-';

		const lowerField = field.toLowerCase();
		if (lowerField.includes('date') || lowerField.includes('fecha')) {
			// If value is already formatted as DD/MM/YYYY, return as is
			const datePattern = /^\d{2}\/\d{2}\/\d{4}$/;
			if (typeof value === 'string' && datePattern.test(value)) {
				return value;
			}
			// If it's an ISO date or Date object, format it
			return this.dateService.formatToDDMMYYYY(value instanceof Date ? value.toISOString() : String(value));
		}

		if (typeof value === 'number' && !Number.isNaN(value)) {
			return value.toLocaleString(activeLocale(this.translocoService), {
				minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
				maximumFractionDigits: 2,
			});
		}

		// Also handle numeric strings (e.g. "22150.34")
		if (
			typeof value === 'string' &&
			!Number.isNaN(Number(value)) &&
			value.trim() !== '' &&
			!lowerField.includes('id') &&
			!lowerField.includes('phone') &&
			!lowerField.includes('tel')
		) {
			const num = Number(value);
			return num.toLocaleString(activeLocale(this.translocoService), {
				minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
				maximumFractionDigits: 2,
			});
		}

		return String(value);
	}
}
